// web/src/services/firestore/taskService.js
import { db } from '@/firebase'
import {
  collection,
  doc,
  query,
  orderBy,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  serverTimestamp,
  where,
} from 'firebase/firestore'
import { listUsersFS } from '@/services/firestore/usersService'
import { listCommunitiesFS } from '@/services/firestore/communityService'

/* ---------------- helpers ---------------- */



const lc = (s) => (s || '').toString().trim().toLowerCase()
const norm = (s) => lc(s).replace(/\s+/g, ' ')

function getSessionSafe() {
  try {
    return JSON.parse(localStorage.getItem('session') || 'null')
  } catch {
    return null
  }
}

function buildTaskCommunitySet(task) {
  return new Set(
    [task?.communityCode, task?.communityName, task?.community]
      .filter(Boolean)
      .map(lc)
  )
}

function buildUserCommunitySet(user) {
  if (user?.allCommunities === true) return null
  return new Set(
    [
      ...(Array.isArray(user?.communities) ? user.communities : []),
      ...(Array.isArray(user?.communityCodes) ? user.communityCodes : []),
      user?.communityCode,
      user?.communityName,
      user?.community,
    ]
      .filter(Boolean)
      .map(lc)
  )
}

function userMatchesTaskCommunity(user, task) {
  if (user?.allCommunities === true) return true
  const taskCommunities = buildTaskCommunitySet(task)
  if (!taskCommunities.size) return true
  const userCommunities = buildUserCommunitySet(user)
  return Array.from(taskCommunities).some((community) =>
    userCommunities.has(community)
  )
}

function userMatchesTaskApprovalRole(user, task) {
  const requiredRoleKeyNorm = lc(task?.requiredRoleKey || '')
  const requiredRoleLabelNorm = norm(task?.requiredRole || '')
  if (!requiredRoleKeyNorm && !requiredRoleLabelNorm) return false
  if (!userMatchesTaskCommunity(user, task)) return false

  const roleKeys = [
    ...(Array.isArray(user?.approverRoles) ? user.approverRoles : []),
    ...(Array.isArray(user?.roles) ? user.roles : []),
  ]
    .map(lc)
    .filter(Boolean)

  if (requiredRoleKeyNorm && roleKeys.includes(requiredRoleKeyNorm)) {
    return true
  }

  const titleNorm = norm(user?.title || '')
  return !!titleNorm && !!requiredRoleLabelNorm && titleNorm === requiredRoleLabelNorm
}

async function findCommunityConfigForTask(task) {
  const code = lc(task?.communityCode || '')
  const name = lc(task?.communityName || task?.community || '')
  const rows = await listCommunitiesFS()
  for (const community of rows || []) {
    const rowCode = lc(community.code || community.communityCode || community.id || '')
    const rowName = lc(
      community.communityName || community.name || community.code || community.id || ''
    )
    if ((code && rowCode === code) || (name && rowName === name)) {
      return community
    }
  }
  return null
}

function getOwnerInfoFromTask(task) {
  const createdEmail = lc(task?.createdByEmail || task?.createdBy?.email || '')
  const createdName = String(
    task?.createdByName || task?.createdBy?.displayName || ''
  ).trim()
  if (createdEmail) {
    return {
      email: createdEmail,
      name: createdName || createdEmail,
    }
  }

  const assigneeEmail = lc(task?.assigneeEmail || '')
  const assigneeName = String(task?.assigneeName || '').trim()
  if (assigneeEmail) {
    return {
      email: assigneeEmail,
      name: assigneeName || assigneeEmail,
    }
  }

  return null
}

async function resolvePreferredApprovalAssigneeFS(task) {
  if (lc(task?.assigneeEmail)) {
    return {
      email: lc(task.assigneeEmail),
      name: String(task?.assigneeName || '').trim(),
    }
  }

  const type = lc(task?.type || '')
  const status = lc(task?.status || '')
  if (type !== 'approval' || status !== 'awaiting_approval') return null

  const users = (await listUsersFS()).filter((user) => user?.isActive !== false)
  const matches = users
    .filter((user) => userMatchesTaskApprovalRole(user, task))
    .map((user) => ({
      email: lc(user.email),
      name: String(user.displayName || user.name || user.email || '').trim(),
    }))
    .filter((user) => user.email)

  if (matches.length === 1) return matches[0]

  const requiredRoleKeyNorm = lc(task?.requiredRoleKey || '')
  const requiredRoleLabelNorm = norm(task?.requiredRole || '')
  const isManagerStage =
    requiredRoleKeyNorm === 'manager' || requiredRoleLabelNorm === 'manager'

  if (!matches.length && isManagerStage) {
    const community = await findCommunityConfigForTask(task)
    const interimManagerEmail = lc(community?.interimManagerEmail || '')
    if (!interimManagerEmail) return null

    const interimUser = users.find((user) => {
      return (
        lc(user?.email) === interimManagerEmail &&
        user?.isActive !== false &&
        userMatchesTaskCommunity(user, task)
      )
    })

    if (interimUser) {
      return {
        email: interimManagerEmail,
        name: String(
          interimUser.displayName || interimUser.name || interimUser.email || ''
        ).trim(),
      }
    }
  }

  return null
}

/**
 * Normalize a Firestore task document into the shape the UI expects.
 * This includes:
 *  - CAF id/number wiring
 *  - relatedLabel defaulting to CAF number
 *  - requiredRoleKey + requiredRole (label)
 */
function mapTaskDoc(d) {
  const data = d.data() || {}

  const audience = Array.isArray(data.audience)
    ? data.audience
    : (data.audience ? [data.audience] : [])

  // CAF linkage + friendly label fallback
  const rawCafId = data.cafId || data.relatedCafId || ''
  const rawCafNumber = data.cafNumber || data.relatedCafNumber || ''
  const rawRelatedLabel = data.relatedLabel || ''

  const cafId = rawCafId || rawCafNumber
  const cafNumber = rawCafNumber || rawCafId
  const relatedLabel = rawRelatedLabel || cafNumber || cafId || ''

  const communityCode = data.communityCode || data.cafCommunityCode || ''
  const communityName = data.communityName || data.community || ''

  // Role key + label with backwards-compat fallbacks
  const requiredRoleKey =
    data.requiredRoleKey ||
    data.required_role_key ||
    data.stageKey ||
    data.stage_key ||
    ''

  const requiredRoleLabel =
    data.requiredRole || // preferred
    data.requiredRoleLabel ||
    data.required_role ||
    data.stageLabel ||
    data.stage_label ||
    ''

  return {
    id: d.id,

    title: data.title || '',
    note: [data.note, data.assignmentIssue].filter(Boolean).join(' · '),
    instructions: data.instructions || '',
    outcome: data.outcome || null,
    review: data.review || null,
    reviewerEmails: data.reviewerEmails || [],
    referenceLinks: Array.isArray(data.referenceLinks) ? data.referenceLinks : [],
    managedCityMatch: data.managedCityMatch === true,
    assigneeEmails: data.assigneeEmails || [],
    type: data.type || '',
    status: data.status || 'open',

    // CAF linkage
    cafId,
    cafNumber,

    // generic related link
    relatedUrl: data.relatedUrl || '',
    relatedLabel,

    // approval routing
    requiredRole: requiredRoleLabel, // user-friendly label for UI
    requiredRoleKey, // stable key for logic

    // assignment
    assigneeEmail: data.assigneeEmail || data.assignedEmail || '',
    assigneeName: data.assigneeName || data.assignedName || '',

    // creator
    createdByEmail:
      data.createdByEmail ||
      (data.createdBy && data.createdBy.email) ||
      '',
    createdByName:
      data.createdByName ||
      (data.createdBy && data.createdBy.displayName) ||
      '',

    // community scoping
    communityId: data.communityId || data.community || '',
    communityCode,
    communityName,

    audience,

    // timing
    dueAt: data.dueAt || null,
    completedAt: data.completedAt || null,
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,

    // password-reset specific
    targetEmail: data.targetEmail || '',
  }
}

const tasksCol = collection(db, 'tasks')

/* ---------------- core list helpers ---------------- */

/**
 * Base listing: Firestore security rules decide which
 * docs each user can actually see.
 */
export async function listAllVisibleTasks() {
  const q = query(tasksCol, orderBy('createdAt', 'desc'))
  const snap = await getDocs(q)
  const rows = snap.docs.map(mapTaskDoc)
  if (import.meta.env.DEV) {
    console.log('[taskService] listAllVisibleTasks ->', rows.length, 'rows')
  }
  return rows
}

/** Back-compat alias */
export async function listTasks() {
  return listAllVisibleTasks()
}

/**
 * Back-compat for older callers that expect listTasksFS.
 * We ignore filter args and rely on client-side filtering + rules.
 */
export async function listTasksFS(/* filters = {} */) {
  return listAllVisibleTasks()
}

/** Open-ish tasks (client-side filter on top of visible set) */
export async function listOpenTasks() {
  const all = await listAllVisibleTasks()
  const openStates = new Set([
    'open',
    'awaiting_approval',
    'in_review',
    'changes_requested',
    'approved',
  ])
  return all.filter((t) => openStates.has(lc(t.status)))
}

/** Tasks where I’m assignee or creator */
export async function listMyTasks() {
  const sess = getSessionSafe()
  const myEmail = lc(sess?.email)
  const all = await listAllVisibleTasks()

  return all.filter((t) => {
    const assigned = lc(t.assigneeEmail) === myEmail
    const created = lc(t.createdByEmail) === myEmail
    return assigned || created
  })
}

export async function listMyOpenTasks() {
  const my = await listMyTasks()
  const openStates = new Set([
    'open',
    'awaiting_approval',
    'in_review',
    'changes_requested',
    'approved',
  ])
  return my.filter((t) => openStates.has(lc(t.status)))
}

/** Approval items waiting on me (legacy; kept for safety) */
export async function listAwaitingMyApprovalTasks() {
  const my = await listMyTasks()
  return my.filter(
    (t) => lc(t.type) === 'approval' && lc(t.status) === 'awaiting_approval'
  )
}

/** Admin-focused open tasks (only if current user is admin/sysconfig) */
export async function listAdminOpenTasks() {
  const sess = getSessionSafe()
  const lvl = lc(sess?.adminLevel)
  const roles = (Array.isArray(sess?.roles) ? sess?.roles : []).map(lc)
  const isAdmin =
    lvl === 'admin' ||
    lvl === 'sysconfig' ||
    roles.includes('admin') ||
    roles.includes('sysconfig')

  if (!isAdmin) return []

  return listOpenTasks()
}

/* ---------------- create / update helpers ---------------- */


async function updateTaskFS(taskId, patch) {
  const ref = doc(db, 'tasks', taskId)
  await updateDoc(ref, {
    ...patch,
    updatedAt: serverTimestamp(),
  })
}

export async function refreshTaskAssignmentFS(taskInput) {
  const taskId =
    typeof taskInput === 'string'
      ? taskInput
      : (taskInput?.id || '')
  if (!taskId) return null

  const snap = await getDoc(doc(db, 'tasks', taskId))
  if (!snap.exists()) return null
  const task = mapTaskDoc(snap)
  const cafId = task.cafId || ''

  if (cafId) {
    await closeLegacyCompletionTaskVariants(cafId)
    const refreshed = await getDoc(doc(db, 'tasks', taskId))
    if (!refreshed.exists()) return task
    const refreshedTask = mapTaskDoc(refreshed)
    if (lc(refreshedTask.status) === 'closed' && lc(task.type) === 'completion') {
      return refreshedTask
    }
  }

  const currentSnap = await getDoc(doc(db, 'tasks', taskId))
  if (!currentSnap.exists()) return task
  const currentTask = mapTaskDoc(currentSnap)
  const typeLc = lc(currentTask.type)
  const statusLc = lc(currentTask.status)

  if (typeLc !== 'approval' && typeLc !== 'completion') return task

  if (statusLc === 'awaiting_approval') {
    const assignee = await resolvePreferredApprovalAssigneeFS(currentTask)
    const patch = {
      assigneeEmail: assignee?.email || '',
      assigneeName: assignee?.name || '',
      updatedAt: serverTimestamp(),
    }
    await updateDoc(doc(db, 'tasks', taskId), patch)

    return {
      ...currentTask,
      ...patch,
    }
  }

  if ((statusLc === 'approved' || statusLc === 'complete') && currentTask.cafId) {
    const caf = await loadLatestCaf({ id: currentTask.cafId })
    const owner = getCafOwnerInfo(caf, null, currentTask)
    const patch = {
      assigneeEmail: owner?.email || '',
      assigneeName: owner?.name || '',
      type: 'approval',
      updatedAt: serverTimestamp(),
    }
    await updateDoc(doc(db, 'tasks', taskId), patch)

    return {
      ...currentTask,
      ...patch,
    }
  }

  return currentTask
}

// NEW: generic complete helper (used by MyTasks)
export async function completeTaskFS(taskId, extraPatch = {}) {
  if (!taskId) return
  await updateTaskFS(taskId, {
    status: 'complete',
    completedAt: serverTimestamp(),
    ...extraPatch,
  })
}

export async function createTaskFS(task) {
  const now = serverTimestamp()
  const docData = {
    ...task,
    createdAt: task.createdAt || now,
    updatedAt: task.updatedAt || now,
  }

  const ref = await addDoc(tasksCol, docData)
  if (import.meta.env.DEV) {
    console.log('[taskService] createTaskFS ->', ref.id, docData)
  }
  return ref.id
}

/**
 * Explicit helper to mark a password reset task complete (by id).
 * Used by:
 *   - MyTasks UI button
 *   - Admin tools once temp password is set.
 */
export async function completePasswordResetTask(taskId) {
  if (!taskId) return
  return updateTaskFS(taskId, {
    status: 'complete',
    completedAt: serverTimestamp(),
  })
}

/**
 * Auto-close all open password reset tasks for a specific email.
 * Call this from the admin “set temp password” flow.
 */
export async function completePasswordResetTasksForEmail(targetEmail) {
  const email = lc(targetEmail)
  if (!email) return 0

  // Query by targetEmail; filter by type/status in JS
  const q = query(tasksCol, where('targetEmail', '==', email))
  const snap = await getDocs(q)

  const updates = []
  snap.forEach((docSnap) => {
    const data = docSnap.data() || {}
    if (lc(data.type) !== 'password_reset') return
    const status = lc(data.status || 'open')
    if (status === 'complete' || status === 'closed' || status === 'done') {
      return
    }
    updates.push(
      updateTaskFS(docSnap.id, {
        status: 'complete',
        completedAt: serverTimestamp(),
      })
    )
  })

  await Promise.all(updates)
  return updates.length
}

/** find the (single) CAF task for this caf, if any */
async function findCafTaskId(caf) {
  const cafId = caf?.id || caf?.cafId || caf?.number || caf?.cafNumber || ''
  if (!cafId) return null

  const rows = await listCafTaskRecords(cafId)
  return rows[0]?.id || null
}

async function findCafTaskRecord(caf) {
  const taskId = await findCafTaskId(caf)
  if (!taskId) return null
  const snap = await getDoc(doc(db, 'tasks', taskId))
  if (!snap.exists()) return null
  return mapTaskDoc(snap)
}

function getTaskSortValue(task) {
  const created =
    task?.createdAt?.toMillis?.() ||
    (typeof task?.createdAt?.seconds === 'number'
      ? task.createdAt.seconds * 1000
      : 0)
  const updated =
    task?.updatedAt?.toMillis?.() ||
    (typeof task?.updatedAt?.seconds === 'number'
      ? task.updatedAt.seconds * 1000
      : 0)
  return Math.max(created, updated, 0)
}

async function listCafTaskRecords(cafId) {
  if (!cafId) return []
  const q = query(tasksCol, where('cafId', '==', cafId))
  const snap = await getDocs(q)
  if (snap.empty) return []
  return snap.docs
    .map(mapTaskDoc)
    .sort((a, b) => getTaskSortValue(b) - getTaskSortValue(a))
}

function isProjectCompletionTask(task) {
  const title = String(task?.title || '').trim().toLowerCase()
  const note = String(task?.note || '').trim().toLowerCase()
  return (
    title.startsWith('complete project for:') ||
    note.includes('awaiting project completion entry') ||
    lc(task?.type) === 'completion'
  )
}

async function closeLegacyCompletionTaskVariants(cafId) {
  const rows = await listCafTaskRecords(cafId)
  const closeoutRows = rows.filter((row) => row.id && isProjectCompletionTask(row))
  const canonicalCloseout = closeoutRows.find((row) => lc(row.type) !== 'completion')
  if (!canonicalCloseout?.id) return 0

  const staleRows = closeoutRows.filter((row) => row.id !== canonicalCloseout.id && lc(row.type) === 'completion')
  if (!staleRows.length) return 0

  const updates = staleRows.filter((row) => !['closed', 'done'].includes(lc(row.status)))
    .map((row) =>
      updateTaskFS(row.id, {
        status: 'closed',
        note: row.note
          ? `${row.note} | Superseded by current project completion task`
          : 'Superseded by current project completion task',
        requiredRoleKey: '',
        requiredRole: '',
      })
    )

  await Promise.all(updates)
  return updates.length
}

/**
 * Small helper: who originally created this CAF?
 * (Used to route tasks back to submitter)
 */
function getCafOwnerInfo(caf, sessionOverride, taskFallback) {
  const sess = sessionOverride || getSessionSafe() || {}
  const c = caf || {}

  const taskOwner = getOwnerInfoFromTask(taskFallback)
  if (taskOwner?.email) {
    return taskOwner
  }

  const email =
    c.createdByEmail ||
    (c.createdBy && c.createdBy.email) ||
    sess.email ||
    ''
  const name =
    c.createdByName ||
    (c.createdBy && c.createdBy.displayName) ||
    sess.displayName ||
    sess.name ||
    email

  return {
    email: (email || '').toLowerCase(),
    name: name || email || 'User',
  }
}

/**
 * Normalize CAF id/number + related URL/label in one place.
 */
function getCafMeta(caf) {
  const cafId = caf.id || caf.cafId || caf.number || caf.cafNumber || ''
  const cafNumber = caf.number || caf.cafNumber || cafId
  const relatedUrl = cafId ? `/dev/caf/${encodeURIComponent(cafId)}` : ''
  const relatedLabel = cafNumber || ''
  return { cafId, cafNumber, relatedUrl, relatedLabel }
}

/**
 * Reload the latest CAF doc from Firestore to avoid using stale
 * in-memory copies when updating tasks after an approve/send-back.
 */
async function loadLatestCaf(cafLike) {
  const cafId =
    cafLike?.id || cafLike?.cafId || cafLike?.number || cafLike?.cafNumber
  if (!cafId) return cafLike
  try {
    const ref = doc(db, 'caf', cafId)
    const snap = await getDoc(ref)
    if (snap.exists()) {
      return { id: snap.id, ...snap.data() }
    }
  } catch (e) {
    if (import.meta.env.DEV) {
      console.warn('[taskService] loadLatestCaf failed, falling back to input', e)
    }
  }
  return cafLike
}

/**
 * Read stage information from the CAF in a way that's
 * compatible with both new (stageKey/stageLabel) and old
 * (stage/currentStage) documents.
 *
 * stageKey is the stable key (e.g. "regional-manager") from approvalLimits doc id.
 * stageLabel is the friendly text (e.g. "Regional Manager").
 */
function getStageInfoFromCaf(caf) {
  const stageKey =
    (caf.stageKey || caf.stage_key || caf.stageKeyRaw || '').toString().trim()

  const stageLabel =
    caf.stageLabel ||
    caf.stage ||
    caf.currentStage ||
    stageKey ||
    ''

  return { stageKey, stageLabel }
}

function getCafWorkflowDisplayLabel(caf) {
  const title = String(caf?.title || '').trim()
  const number = String(caf?.number || caf?.id || '').trim()
  const base = title || number || 'CAF'
  if (caf?.isAmendment === true) {
    const amendmentNumber = caf?.amendmentNumber ? `Amendment ${caf.amendmentNumber}` : 'Amendment'
    const parent = caf?.parentCafNumber || caf?.rootCafNumber || ''
    return parent ? `${base} (${amendmentNumber} to ${parent})` : `${base} (${amendmentNumber})`
  }
  return base
}

/* ---------------- CAF draft + generic tasks ---------------- */

/**
 * Draft task for a new CAF.
 * Called from CafNew.jsx after a CAF draft is created.
 */
export async function createTaskForCafDraftFS(cafLike, sessionOverride) {
  const owner = getCafOwnerInfo(cafLike, sessionOverride)

  // cafLike can be a CAF object or just an ID/number
  const caf =
    cafLike && typeof cafLike === 'object'
      ? cafLike
      : { id: cafLike, number: cafLike }

  const { cafId, cafNumber, relatedUrl } = getCafMeta(caf)

  const label = getCafWorkflowDisplayLabel(caf)
  const title = label ? `CAF Draft: ${label}` : 'CAF Draft'

  const note = caf?.isAmendment === true
    ? 'Draft CAF amendment awaiting completion'
    : 'Draft CAF awaiting completion'

  const taskPayload = {
    title,
    note,
    type: 'draft',
    status: 'open',

    cafId,
    cafNumber,

    relatedUrl,
    // show CAF number instead of "Open CAF"
    relatedLabel: cafNumber || 'CAF Draft',

    communityCode: caf.communityCode || '',
    communityName: caf.communityName || caf.community || '',

    assigneeEmail: owner.email,
    assigneeName: owner.name,

    createdByEmail: owner.email,
    createdByName: owner.name,
    createdBy: {
      email: owner.email,
      displayName: owner.name,
    },

    requiredRoleKey: '',
    requiredRole: '',

    audience: [],
  }

  return createTaskFS(taskPayload)
}

/**
 * Password reset request task.
 * Imported by SignIn.jsx as createPasswordResetTask.
 *
 * input can be:
 *   - string: target email
 *   - { userEmail, link }
 *   - { requesterEmail, requesterName, targetEmail, reason, communityName, link }
 */
export async function createPasswordResetTask(input) {
  const sess = getSessionSafe() || {}

  const isObj = input && typeof input === 'object'

  const requesterEmail = String(
    (isObj && (input.requesterEmail || sess.email)) || sess.email || ''
  ).toLowerCase()

  const requesterName =
    (isObj && input.requesterName) ||
    sess.displayName ||
    sess.name ||
    requesterEmail

  const targetEmailRaw = isObj
    ? input.targetEmail || input.userEmail || requesterEmail
    : input || requesterEmail

  const target = String(targetEmailRaw || requesterEmail).toLowerCase()

  const reason = String(
    (isObj && input.reason) || ''
  ).trim() || 'Not specified'

  const communityName = isObj
    ? input.communityName || sess.communityName || sess.communityCode || '—'
    : sess.communityName || sess.communityCode || '—'

  const link = isObj ? (input.link || '') : ''

  const title = 'Password reset requested'

  const noteParts = [
    `Password reset requested for ${target}`,
  ]

  if (requesterEmail && requesterEmail !== target) {
    noteParts.push(`by ${requesterEmail}`)
  }

  if (reason) {
    noteParts.push(`Reason: ${reason}`)
  }

  if (communityName && communityName !== '—') {
    noteParts.push(`Community: ${communityName}`)
  }

  const note = noteParts.join(' | ')

  const taskPayload = {
    title,
    note,
    type: 'password_reset',
    status: 'open',

    // no CAF context
    cafId: '',
    cafNumber: '',

    communityCode: '',
    communityName,

    assigneeEmail: '',
    assigneeName: '',

    createdByEmail: requesterEmail,
    createdByName: requesterName,
    createdBy: {
      email: requesterEmail,
      displayName: requesterName,
    },

    audience: ['admin'],

    // who this reset is really for (used by auto-close)
    targetEmail: target,

    // optional admin link from SignIn.jsx
    ...(link
      ? {
          relatedUrl: link,
          relatedLabel: 'Open User Admin',
        }
      : {}),
  }

  return createTaskFS(taskPayload)
}

export async function createArchiveRequestTaskFS({
  entityType = '',
  entityTitle = '',
  communityCode = '',
  communityName = '',
  relatedUrl = '',
  relatedLabel = '',
  reason = '',
} = {}) {
  const sess = getSessionSafe() || {}
  const requesterEmail = String(sess.email || '').toLowerCase()
  const requesterName =
    sess.displayName || sess.name || requesterEmail || 'User'

  const normalizedEntityType = String(entityType || '').trim() || 'item'
  const normalizedEntityTitle = String(entityTitle || '').trim() || 'Untitled'
  const normalizedReason = String(reason || '').trim()

  const noteParts = [
    `Archive/soft delete requested for ${normalizedEntityType}: ${normalizedEntityTitle}`,
    `Requested by ${requesterName}${requesterEmail ? ` (${requesterEmail})` : ''}`,
  ]

  if (communityName || communityCode) {
    noteParts.push(`Community: ${communityName || communityCode}`)
  }
  if (normalizedReason) {
    noteParts.push(`Reason: ${normalizedReason}`)
  }

  return createTaskFS({
    title: `Archive request: ${normalizedEntityTitle}`,
    note: noteParts.join(' | '),
    type: 'admin_request',
    status: 'open',
    cafId: '',
    cafNumber: '',
    relatedUrl: relatedUrl || '',
    relatedLabel: relatedLabel || normalizedEntityTitle,
    communityCode: communityCode || '',
    communityName: communityName || '',
    assigneeEmail: '',
    assigneeName: '',
    createdByEmail: requesterEmail,
    createdByName: requesterName,
    createdBy: {
      email: requesterEmail,
      displayName: requesterName,
    },
    audience: ['admin'],
  })
}

/* ---------------- data-driven approval routing ---------------- */

// (rest of file unchanged below this point)

export async function createApprovalTaskForStage(cafInput) {
  if (!cafInput) return

  // Always use the latest CAF doc
  const caf = await loadLatestCaf(cafInput)
  if (!caf) return

  const { cafId, cafNumber, relatedUrl, relatedLabel } = getCafMeta(caf)
  if (!cafId) return

  const { stageKey, stageLabel } = getStageInfoFromCaf(caf)
  const community = (caf.communityCode || '').toString()

  if (!stageKey && !stageLabel) {
    console.warn('createApprovalTaskForStage: missing stage info for CAF', cafId)
    return
  }

  const stageKeyNorm = lc(stageKey)
  const stageLabelNorm = norm(stageLabel)

  let users
  try {
    users = await listUsersFS()
  } catch (e) {
    console.warn(
      '[taskService] createApprovalTaskForStage: unable to list users; ' +
        'likely Firestore rules blocking non-admin /users list. ' +
        'Skipping per-user approval tasks.',
      e
    )
    return
  }

  const approvers = users.filter((u) => {
    if (!u?.isActive) return false

    const uCommunities = new Set(
      [
        ...(Array.isArray(u.communities) ? u.communities : []),
        u.communityCode,
        u.community,
      ]
        .filter(Boolean)
        .map(String)
    )
    const inCommunity = !community || uCommunities.has(community)
    if (!inCommunity) return false

    const roleKeys = (Array.isArray(u.approverRoles) ? u.approverRoles : [])
      .map((k) => lc(k))
      .filter(Boolean)

    if (stageKeyNorm && roleKeys.length && roleKeys.includes(stageKeyNorm)) {
      return true
    }

    const titleNorm = norm(u.title || '')
    if (!titleNorm) return false

    if (stageLabelNorm && titleNorm === stageLabelNorm) {
      return true
    }

    return false
  })

  if (!approvers.length) {
    console.warn(
      'createApprovalTaskForStage: no approvers for stage',
      stageKey || stageLabel,
      'community',
      community
    )
    return
  }

  for (const user of approvers) {
    const assigneeEmail = (user.email || '').toLowerCase()

    await createTaskFS({
      title: cafNumber
        ? `CAF Awaiting Your Approval: ${cafNumber}`
        : 'CAF Awaiting Your Approval',
      note: `CAF awaiting your approval (${stageLabel || stageKey})`,

      type: 'approval',
      status: 'awaiting_approval',

      cafId,
      cafNumber,
      relatedUrl,
      relatedLabel,

      communityCode: caf.communityCode || '',
      communityName: caf.communityName || caf.community || '',

      assigneeEmail,
      assigneeName: user.displayName || user.name || user.email || '',

      createdByEmail:
        caf.createdByEmail ||
        (caf.createdBy && caf.createdBy.email) ||
        '',
      createdByName:
        caf.createdByName ||
        (caf.createdBy && caf.createdBy.displayName) ||
        '',
      createdBy: {
        email:
          caf.createdByEmail ||
          (caf.createdBy && caf.createdBy.email) ||
          '',
        displayName:
          caf.createdByName ||
          (caf.createdBy && caf.createdBy.displayName) ||
          '',
      },

      requiredRoleKey: stageKey || '',
      requiredRole: stageLabel || stageKey || '',

      audience: [],
    })
  }
}

/* ---------------- CAF workflow → task hooks ---------------- */

export async function onCafSubmittedTasksFS(cafInput, sessionOverride) {
  try {
    if (!cafInput) return

    const caf = await loadLatestCaf(cafInput)
    if (!caf) return

    const existingTask = await findCafTaskRecord(caf)
    const owner = getCafOwnerInfo(caf, sessionOverride, existingTask)
    const { cafId, cafNumber, relatedUrl, relatedLabel } = getCafMeta(caf)
    if (!cafId) return

    const { stageKey, stageLabel } = getStageInfoFromCaf(caf)

    const label = getCafWorkflowDisplayLabel(caf)
    const title = label
      ? `CAF Awaiting Approval: ${label}`
      : 'CAF Awaiting Approval'

    const base = {
      title,
      note: caf?.isAmendment === true ? 'CAF amendment awaiting approval' : 'CAF awaiting approval',
      type: 'approval',
      status: 'awaiting_approval',

      cafId,
      cafNumber,
      relatedUrl,
      relatedLabel,

      communityCode: caf.communityCode || '',
      communityName: caf.communityName || caf.community || '',

      assigneeEmail: '',
      assigneeName: '',

      createdByEmail: owner.email,
      createdByName: owner.name,
      createdBy: {
        email: owner.email,
        displayName: owner.name,
      },

      requiredRoleKey: stageKey || '',
      requiredRole: stageLabel || stageKey || '',

      audience: [],
    }

    if (existingTask?.id) {
      await updateTaskFS(existingTask.id, base)
    } else {
      await createTaskFS(base)
    }
  } catch (e) {
    console.warn('[taskService] onCafSubmittedTasksFS error (non-fatal)', e)
  }
}

export async function refreshCafApprovalRoutingFS(cafInput, sessionOverride) {
  try {
    const caf = await loadLatestCaf(cafInput)
    if (!caf) return null

    const status = lc(caf.status || '')
    if (status !== 'awaiting_approval' && status !== 'in_review') {
      return null
    }

    return onCafSubmittedTasksFS(caf, sessionOverride)
  } catch (e) {
    console.warn('[taskService] refreshCafApprovalRoutingFS error (non-fatal)', e)
    return null
  }
}

export async function onCafSentBackTasksFS(cafInput, sessionOverride) {
  try {
    if (!cafInput) return

    const caf = await loadLatestCaf(cafInput)
    if (!caf) return

    const { cafId, cafNumber, relatedUrl, relatedLabel } = getCafMeta(caf)
    if (!cafId) return

    const existingTask = await findCafTaskRecord(caf)
    const owner = getCafOwnerInfo(caf, sessionOverride, existingTask)

    const base = {
      title: caf.title
        ? `CAF Changes Requested: ${getCafWorkflowDisplayLabel(caf)}`
        : (cafNumber
            ? `CAF Changes Requested: ${getCafWorkflowDisplayLabel(caf)}`
            : 'CAF Changes Requested'),
      note:
        'Changes requested — please make the requested updates, then submit the CAF again. Resubmitting restarts the approval chain from the beginning. If you are also part of the approval chain, you will need to approve again at your level after resubmitting.',
      type: 'draft',
      status: 'changes_requested',

      cafId,
      cafNumber,
      relatedUrl,
      relatedLabel,

      communityCode: caf.communityCode || '',
      communityName: caf.communityName || caf.community || '',

      assigneeEmail: owner.email,
      assigneeName: owner.name,

      createdByEmail: owner.email,
      createdByName: owner.name,
      createdBy: {
        email: owner.email,
        displayName: owner.name,
      },

      requiredRoleKey: '',
      requiredRole: '',
    }

    if (existingTask?.id) {
      await updateTaskFS(existingTask.id, base)
      return existingTask.id
    }
    return createTaskFS(base)
  } catch (e) {
    console.warn('[taskService] onCafSentBackTasksFS error (non-fatal)', e)
  }
}

export async function onCafFinalApprovedTasksFS(cafInput, sessionOverride) {
  try {
    if (!cafInput) return

    const caf = await loadLatestCaf(cafInput)
    if (!caf) return
    const existingTask = await findCafTaskRecord(caf)
    const owner = getCafOwnerInfo(caf, sessionOverride, existingTask)
    const { cafId, cafNumber, relatedUrl, relatedLabel } = getCafMeta(caf)

    if (!existingTask?.id) {
      const createdId = await createTaskFS({
        title: caf.title
          ? `Complete project for: ${getCafWorkflowDisplayLabel(caf)}`
          : cafNumber
            ? `Complete project for: ${getCafWorkflowDisplayLabel(caf)}`
            : 'Complete project',
        note: caf?.isAmendment === true
          ? 'CAF amendment fully approved – awaiting project completion entry'
          : 'CAF fully approved – awaiting project completion entry',
        type: 'approval',
        status: 'approved',
        cafId,
        cafNumber,
        relatedUrl,
        relatedLabel,
        communityCode: caf.communityCode || '',
        communityName: caf.communityName || caf.community || '',
        assigneeEmail: owner.email,
        assigneeName: owner.name,
        createdByEmail: owner.email,
        createdByName: owner.name,
        createdBy: {
          email: owner.email,
          displayName: owner.name,
        },
        requiredRoleKey: '',
        requiredRole: '',
        audience: [],
      })
      await closeLegacyCompletionTaskVariants(cafId)
      return createdId
    }

    await updateTaskFS(existingTask.id, {
      status: 'approved',
      type: 'approval',
      assigneeEmail: owner.email,
      assigneeName: owner.name,
      requiredRoleKey: '',
      requiredRole: '',
      note: caf?.isAmendment === true
        ? 'CAF amendment fully approved – awaiting project completion entry'
        : 'CAF fully approved – awaiting project completion entry',
    })
    await closeLegacyCompletionTaskVariants(cafId)
    return existingTask.id
  } catch (e) {
    console.warn('[taskService] onCafFinalApprovedTasksFS error (non-fatal)', e)
  }
}

export async function onCafCompletedTasksFS(cafInput) {
  try {
    if (!cafInput) return

    const caf = await loadLatestCaf(cafInput)
    if (!caf) return

    const existingId = await findCafTaskId(caf)
    if (!existingId) return

    const existingTask = await findCafTaskRecord(caf)
    const owner = getCafOwnerInfo(caf, null, existingTask)

    await updateTaskFS(existingId, {
      status: 'complete',
      type: 'approval',
      assigneeEmail: owner?.email || '',
      assigneeName: owner?.name || '',
      requiredRoleKey: '',
      requiredRole: '',
      completedAt: serverTimestamp(),
    })
    await closeLegacyCompletionTaskVariants(caf.id || caf.cafId || '')
    return existingId
  } catch (e) {
    console.warn('[taskService] onCafCompletedTasksFS error (non-fatal)', e)
  }
}
